import { auditOutcome, type AuditOutcome } from '@occ/contracts';
import { get, type Query } from '../api/http';
import { ROUTES } from '../api/routes';

/**
 * Bitácora (Módulo 10, RF-09).
 *
 * `GET /audit` devuelve una página `{entries, nextCursor}` con el contrato
 * `auditPage` de `@occ/contracts`. Esta capa sigue leyendo con tolerancia porque
 * la pantalla tiene que poder enseñar algo aunque el backend cambie de forma, pero
 * el nombre del campo **no** es negociable y hubo que corregirlo: esto leía
 * `payload.events` y el backend devuelve `payload.entries`, así que la tabla salía
 * vacía con una respuesta 200 perfectamente válida — el peor modo de fallo, porque
 * "no hay nada registrado" y "no sé leer la respuesta" se ven exactamente igual.
 *
 * La exportación se genera en el navegador y no se pide al backend: es un CSV de
 * lo que hay en pantalla, con los mismos filtros aplicados, y así no hace falta
 * un segundo endpoint que pueda discrepar de la tabla que el técnico está viendo.
 */

export type AuditChannel = 'panel' | 'mcp' | 'api' | 'system';

export type AuditRow = {
  /** El backend puede no devolverlo; entonces se sintetiza para la clave de React. */
  id: string;
  occurredAt: Date | null;
  actorUserId: string | null;
  actorEmail: string | null;
  channel: AuditChannel;
  action: string;
  nodeId: string | null;
  nodeAlias: string | null;
  sessionId: string | null;
  outcome: AuditOutcome;
  reason: string | null;
  durationMs: number | null;
  detail: Record<string, unknown>;
};

/**
 * Tope de filas por página que acepta el backend (`listAuditQuery`).
 *
 * Se repite aquí en lugar de importarse porque el esquema es de validación y no
 * expone la constante. Pedir más devuelve **400 y ninguna fila**: es exactamente
 * lo que pasaba con el 500 que había antes, y el error salía en la propia bitácora
 * como `GET /audit` con `outcome: error`, que fue lo que lo delató.
 */
const MAX_FILAS = 200;

export type AuditFilters = {
  actorUserId?: string;
  nodeId?: string;
  /**
   * Solo del lado del panel: **el backend no filtra por cliente**, y el nodo es la
   * unidad con la que se pregunta. Sirve para acotar la lista de nodos del
   * desplegable, que es lo útil cuando hay muchos clientes. No viaja en la
   * consulta: un filtro que se manda y se ignora es peor que uno que no existe,
   * porque el resultado parece filtrado.
   */
  clientId?: string;
  action?: string;
  outcome?: AuditOutcome | '';
  /** Fechas en formato `yyyy-mm-dd`, tal como las da un `<input type="date">`. */
  from?: string;
  to?: string;
  limit?: number;
};

const CHANNELS: AuditChannel[] = ['panel', 'mcp', 'api', 'system'];

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseRow = (value: unknown, index: number): AuditRow => {
  const record = asRecord(value);
  const outcome = auditOutcome.safeParse(record.outcome);
  const channel = record.channel;

  return {
    id: asText(record.id) ?? `fila-${index}`,
    occurredAt: asDate(record.occurredAt),
    actorUserId: asText(record.actorUserId),
    // Campo de conveniencia: si el backend resuelve el correo del actor, la tabla
    // lo enseña; si no, queda el uuid, que sirve para filtrar aunque no se lea.
    actorEmail: asText(record.actorEmail) ?? asText(asRecord(record.actor).email),
    channel: CHANNELS.includes(channel as AuditChannel) ? (channel as AuditChannel) : 'api',
    action: asText(record.action) ?? '—',
    nodeId: asText(record.nodeId),
    nodeAlias: asText(record.nodeAlias),
    sessionId: asText(record.sessionId),
    outcome: outcome.success ? outcome.data : 'error',
    reason: asText(record.reason),
    durationMs: typeof record.durationMs === 'number' ? record.durationMs : null,
    detail: asRecord(record.detail),
  };
};

const toQuery = (filters: AuditFilters): Query => ({
  actorUserId: filters.actorUserId || undefined,
  nodeId: filters.nodeId || undefined,
  action: filters.action || undefined,
  outcome: filters.outcome || undefined,
  // El día completo: `from` empieza a las 00:00 y `to` termina a las 23:59:59,
  // que es lo que espera quien escribe la misma fecha en los dos campos.
  from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
  to: filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
  limit: Math.min(filters.limit ?? MAX_FILAS, MAX_FILAS),
});

export const fetchAudit = async (filters: AuditFilters): Promise<AuditRow[]> => {
  const payload = await get<unknown>(ROUTES.audit, undefined, toQuery(filters));
  const items = Array.isArray(payload)
    ? payload
    : (((payload as { entries?: unknown })?.entries ?? []) as unknown[]);
  return items
    .map(parseRow)
    .sort((left, right) => (right.occurredAt?.getTime() ?? 0) - (left.occurredAt?.getTime() ?? 0));
};

// ── Exportación ─────────────────────────────────────────────────────────────

const COLUMNS: Array<{ header: string; of: (row: AuditRow) => string }> = [
  { header: 'fecha', of: (row) => row.occurredAt?.toISOString() ?? '' },
  { header: 'canal', of: (row) => row.channel },
  { header: 'usuario', of: (row) => row.actorEmail ?? row.actorUserId ?? '' },
  { header: 'accion', of: (row) => row.action },
  { header: 'resultado', of: (row) => row.outcome },
  { header: 'nodo', of: (row) => row.nodeAlias ?? row.nodeId ?? '' },
  { header: 'sesion', of: (row) => row.sessionId ?? '' },
  { header: 'duracion_ms', of: (row) => (row.durationMs === null ? '' : String(row.durationMs)) },
  { header: 'motivo', of: (row) => row.reason ?? '' },
  { header: 'detalle', of: (row) => JSON.stringify(row.detail) },
];

/**
 * Escapado de CSV según RFC 4180.
 *
 * Importa más de lo que parece: el campo `detalle` es JSON y lleva comas y
 * comillas siempre. Sin escapar, cualquier hoja de cálculo desalinearía todas las
 * columnas a partir de la primera fila con detalle.
 */
const escapeCell = (value: string): string => `"${value.replace(/"/g, '""')}"`;

export const auditToCsv = (rows: AuditRow[]): string => {
  const lines = [COLUMNS.map((column) => column.header).join(',')];
  for (const row of rows) {
    lines.push(COLUMNS.map((column) => escapeCell(column.of(row))).join(','));
  }
  return lines.join('\r\n');
};

/**
 * Marca de orden de bytes, por su punto de código y no literal: es un carácter
 * invisible y dentro del fuente nadie sabría que está ahí.
 *
 * Excel la necesita para abrir el CSV como UTF-8; sin ella usa la codificación
 * del sistema y los acentos de los motivos en español salen mal.
 */
const BOM = '\uFEFF';

export const downloadAuditCsv = (rows: AuditRow[]): void => {
  const blob = new Blob([BOM, auditToCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `bitacora-occ-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
};
